<template>
  <div class="form-select-options">
    <draggable
      v-if="options.length > 0"
      :model-value="options"
      :item-key="optionKey"
      handle=".handle"
      :animation="150"
      class="options"
      @update:model-value="onReorder"
    >
      <template #item="{ element: option, index }">
        <div class="option">
          <div v-if="option.type === 'string_const'" class="option-content">
            <HandleIcon class="handle" />
            <ColorsButton
              :value="optionColor(option)"
              @select="(color) => onSelectColor(option, color)"
            />
            <BaseInput
              :ref="(el) => setInputRef(el, index)"
              v-model:value="option.value"
              placeholder="New Option"
              @enter="() => handleOptionEnter(index)"
            />
          </div>
          <BaseButton
            kind="auxiliary"
            class="remove"
            @click="removeOption(index)"
          >
            <template #icon>
              <CloseIcon />
            </template>
          </BaseButton>
        </div>
      </template>
    </draggable>

    <BaseButton text="Add option" kind="auxiliary" @click="addOption">
      <template #icon>
        <AddIcon />
      </template>
    </BaseButton>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  nextTick,
  PropType,
  ref,
  toRefs,
  type ComponentPublicInstance,
} from "vue";
// Importing the package directly breaks the build in vite+rollup prod build
import draggable from "vuedraggable/src/vuedraggable";

import { CalendarColor, GraphCustomType } from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseInput from "@/components/base/BaseInput.vue";
import { getColor } from "@/components/color";
import ColorsButton from "@/components/ColorsButton.vue";
import AddIcon from "@/components/icons/AddIcon.vue";
import CloseIcon from "@/components/icons/CloseIcon.vue";
import HandleIcon from "@/components/icons/HandleIcon.vue";

export default defineComponent({
  components: {
    BaseInput,
    BaseButton,
    ColorsButton,
    AddIcon,
    CloseIcon,
    HandleIcon,
    draggable,
  },
  props: {
    options: {
      type: Array as PropType<Array<GraphCustomType>>,
      required: true,
    },
    // Chosen colors, keyed by option object identity (values are editable,
    // so a value-keyed record would detach on rename). Owned by the modal
    // host (TypeEdition), which materializes it to a value-keyed record on
    // Done; mutated in place here like `options` is.
    colors: {
      type: Map as PropType<Map<GraphCustomType, CalendarColor>>,
      required: true,
    },
  },
  emits: {
    "update:options": (_: Array<GraphCustomType>) => true,
  },
  setup(props, { emit }) {
    const { options, colors } = toRefs(props);

    const optionColor = (option: GraphCustomType): CalendarColor =>
      colors.value.get(option) ??
      getColor(option.type === "string_const" ? option.value : undefined);
    const onSelectColor = (option: GraphCustomType, color: CalendarColor) => {
      colors.value.set(option, color);
    };
    const inputRefs = ref<Record<number, { focus: () => void }>>({});

    // Values are editable and can be duplicated mid-edit (e.g. two empty
    // options), so keys are assigned per option object identity instead.
    let nextOptionKey = 1;
    const optionKeys = new WeakMap<GraphCustomType, number>();
    const optionKey = (option: GraphCustomType): number => {
      let key = optionKeys.get(option);
      if (key === undefined) {
        key = nextOptionKey++;
        optionKeys.set(option, key);
      }
      return key;
    };

    const setInputRef = (
      el: Element | ComponentPublicInstance | null,
      index: number
    ) => {
      if (el && "focus" in el && typeof el.focus === "function") {
        const focus = el.focus;
        inputRefs.value[index] = {
          focus: () => Reflect.apply(focus, el, []),
        };
      }
    };

    const handleOptionEnter = async (index: number) => {
      // Add new option after the current one
      options.value?.splice(index + 1, 0, {
        type: "string_const",
        value: "",
      });
      emit("update:options", options.value);

      // Focus the new input on next tick
      await nextTick();
      const newInputIndex = index + 1;
      const newInput = inputRefs.value[newInputIndex];
      newInput?.focus();
    };

    const addOption = async () => {
      options.value?.push({
        type: "string_const",
        value: "",
      });
      emit("update:options", options.value);

      await nextTick();
      const lastInputIndex = options.value.length - 1;
      const lastInput = inputRefs.value[lastInputIndex];
      lastInput?.focus();
    };

    const removeOption = (index: number) => {
      const filteredOptions = options.value.filter((_, i) => i !== index) || [];
      emit("update:options", filteredOptions);
    };

    const onReorder = (reordered: Array<GraphCustomType>) => {
      emit("update:options", reordered);
    };

    return {
      addOption,
      handleOptionEnter,
      onReorder,
      onSelectColor,
      optionColor,
      optionKey,
      removeOption,
      setInputRef,
    };
  },
});
</script>

<style lang="scss" scoped>
@use "@/styles/inputs" as *;

.options {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;

  .option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;

    .option-content {
      display: flex;
      align-items: center;
      gap: 10px;
      @extend %font-caption-medium;
      @include themed {
        color: t($grey-70);
      }

      .handle {
        cursor: grab;
        margin-inline-end: 4px;
      }
    }

    &.sortable-ghost {
      opacity: 0.4;
    }
  }
}
</style>
